import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import PageShell from '../components/PageShell';
import { listNews } from '../api/news';
import { TOPICS } from '../data/onboarding';
import { NEWS_LABELS, fmtDate, takeOpenNews } from './welcomeBits';
import { playTopic } from './WelcomePage';
import './WelcomePage.css';

/* Le bouton qui ramène à l'Accueil, sur ses quatre sections (prototype du
   2026-10-01). */
export function BackHome({ setPage }) {
  const { t } = useTranslation();
  return (
    <button type="button" className="wch-btn" onClick={() => setPage && setPage('welcome')}>
      <ArrowLeft size={13} strokeWidth={1.8} aria-hidden="true" /> {t('cockpit:home.back')}
    </button>
  );
}

/* Nouveautés (refonte du 2026-10-01) : une nouveauté par rangée, sa date et
   son module à gauche, son titre et sa phrase à droite ; « Ce qui change »
   déplie la liste de ses changements et le guide qui la montre. Celle qu'on
   ouvre depuis l'Accueil arrive dépliée, sinon la plus récente. */
export default function WelcomeNewsPage({ setPage, onOpenPanel }) {
  const { t } = useTranslation();
  const h = (k, o) => t(`cockpit:home.${k}`, o);
  const [news, setNews] = useState(null);
  const [open, setOpen] = useState(() => takeOpenNews());

  useEffect(() => {
    listNews(null, 200).then((r) => {
      const rows = r.data || [];
      setNews(rows);
      setOpen((cur) => cur || (rows[0] && rows[0].changes && rows[0].changes.length ? rows[0].id : null));
    }).catch(() => setNews([]));
  }, []);

  const topics = useMemo(() => new Map(TOPICS.map((tp) => [tp.id, tp])), []);

  return (
    <PageShell title={t('nav:welcomeNews')} actions={<BackHome setPage={setPage} />}>
      <div className="wch">
        {news && news.length === 0 && <p className="wch-empty">{h('noNews')}</p>}
        {news && news.length > 0 && (
          <div className="wch-list">
            {news.map((n) => {
              const isOpen = open === n.id;
              const changes = n.changes || [];
              const topic = n.guide_topic ? topics.get(n.guide_topic) : null;
              return (
                <article className="wch-entry" key={n.id}>
                  <div className="wch-entry__date">
                    {fmtDate(n.news_date, true, true)}
                    <span>{NEWS_LABELS[n.module] || n.module}</span>
                  </div>
                  <div className="wch-entry__copy">
                    <h3>{n.text}</h3>
                    {n.intro && <p>{n.intro}</p>}
                    {(changes.length > 0 || topic) && (
                      <button type="button" className="wch-textlink" aria-expanded={isOpen}
                              onClick={() => setOpen(isOpen ? null : n.id)}>
                        {isOpen ? h('collapse') : h('whatChanges')}
                        {isOpen
                          ? <ChevronUp size={13} strokeWidth={1.8} aria-hidden="true" />
                          : <ChevronDown size={13} strokeWidth={1.8} aria-hidden="true" />}
                      </button>
                    )}
                    {isOpen && changes.length > 0 && (
                      <ul className="wch-entry__changes">{changes.map((c, i) => <li key={i}>{c}</li>)}</ul>
                    )}
                    {isOpen && topic && (
                      <button type="button" className="wch-btn"
                              onClick={() => playTopic(topic, { setPage, openPanel: onOpenPanel })}>
                        {h('seeGuide')} <ArrowRight size={13} strokeWidth={1.8} aria-hidden="true" />
                      </button>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </PageShell>
  );
}
